/* ============================================================================
   Collection AJAX Infinite Scroll — Shopify Studio Theme v15
   assets/infinite-scroll.js

   Replaces numbered pagination with automatic, scroll-driven loading on the
   collection page (section: main-collection-product-grid).

   Detection: a passive, requestAnimationFrame-throttled scroll/resize listener
   that loads the next page when the viewport is within 500px of the bottom of
   the document. (Simple and reliable; can be optimised back to
   IntersectionObserver later if desired.)

   Everything else is unchanged:
     • One request at a time (isLoading guard) + per-URL guard → no duplicates.
     • Advances strictly through `data-next-url` (rendered from paginate.next.url),
       so active filters / sort / search params are always respected.
     • Re-initialises automatically after facets.js replaces #ProductGridContainer
       (filter / sort / search) — detected with a MutationObserver. facets.js is
       NOT modified, and its history.pushState()/back-button behaviour is untouched.
     • Stops (removes the scroll listener) on the last page — no leaks.
     • Numbered pagination stays server-rendered (SEO) and is hidden only when
       this JS runs, so it remains a no-JavaScript fallback.

   No jQuery, no libraries, no global variables (module-scoped IIFE).
   ========================================================================== */

(function () {
  'use strict';

  /** How close to the bottom (px) before the next page is requested. */
  var LOAD_THRESHOLD_PX = 500;

  /** Stable DOM hooks used throughout the controller. */
  var SELECTORS = {
    container: '#ProductGridContainer',
    grid: '#product-grid',
    scope: '[data-infinite-scroll]',
    indicator: '[data-infinite-scroll-indicator]',
    pagination: '.pagination-wrapper',
    gridItem: '.grid__item'
  };

  var CollectionInfiniteScroll = class {
    /**
     * @param {HTMLElement} container - the #ProductGridContainer element.
     */
    constructor(container) {
      this.container = container;
      this.grid = null;             // live <ul id="product-grid">
      this.indicator = null;        // loading indicator wrapper
      this.nextUrl = null;          // URL of the next page, or null on the last page
      this.isLoading = false;       // true while a request is in flight (dupe guard)
      this.generation = 0;          // bumped on every (re)init to void stale fetches
      this.loadedUrls = new Set();  // per-URL guard so a page is never fetched twice
      this.gridObserver = null;     // MutationObserver watching for facets replacement
      this.abortController = null;  // aborts an in-flight fetch when filters change
      this.scrollAttached = false;  // whether the scroll/resize listener is active
      this.scrollTicking = false;   // rAF throttle flag for the scroll handler

      // Seed the guard with the current URL so page 1 can never be re-appended.
      this.loadedUrls.add(this.normalizeUrl(window.location.href));

      // Pre-bind callbacks so add/removeEventListener share a stable identity.
      this.onScroll = this.onScroll.bind(this);
      this.onContainerMutation = this.onContainerMutation.bind(this);
    }

    /**
     * Entry point: initialise against the current grid and start watching the
     * container for facets.js innerHTML replacements.
     */
    start() {
      this.init();

      // childList (NOT subtree): only a full container replacement fires this.
      // Appending cards to #product-grid mutates the grid's children, not the
      // container's direct children, so it never re-triggers initialisation.
      this.gridObserver = new MutationObserver(this.onContainerMutation);
      this.gridObserver.observe(this.container, { childList: true });
    }

    /**
     * Read fresh state from the DOM and (re)arm the scroll listener.
     * Safe to call repeatedly — used on first load and after every filter/sort.
     */
    init() {
      // Invalidate any request from the previous grid so its result is discarded.
      this.generation += 1;
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.isLoading = false;

      // Cache references from the (possibly newly rendered) grid.
      this.grid = this.container.querySelector(SELECTORS.grid);
      var scope = this.container.querySelector(SELECTORS.scope);
      this.indicator = scope ? scope.querySelector(SELECTORS.indicator) : null;
      this.nextUrl = scope ? this.cleanUrl(scope.getAttribute('data-next-url')) : null;

      // JS is running → hide the numbered pagination (kept in the source for SEO).
      this.hidePagination();

      if (!this.grid || !this.nextUrl) {
        // Last page, empty collection, or missing markup → nothing to load.
        this.removeScrollListener();
        return;
      }
      this.attachScrollListener();
      // Handle short pages / already-at-bottom on (re)init without needing a scroll.
      this.maybeLoad();
    }

    /**
     * MutationObserver callback: facets.js swapped the container's innerHTML
     * (filter / sort / search). Detect the brand-new grid node and re-init.
     */
    onContainerMutation() {
      var grid = this.container.querySelector(SELECTORS.grid);
      if (grid && grid !== this.grid) {
        // New URL context (facets.js updated the address via pushState) →
        // reset the per-URL guard to the new base URL, then re-initialise.
        this.loadedUrls = new Set([this.normalizeUrl(window.location.href)]);
        this.init();
      }
    }

    /**
     * Passive scroll/resize handler, throttled to one check per animation frame.
     * All request guarding lives in loadNextPage().
     */
    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      var self = this;
      window.requestAnimationFrame(function () {
        self.scrollTicking = false;
        self.maybeLoad();
      });
    }

    /**
     * Load the next page when the viewport is within LOAD_THRESHOLD_PX of the
     * bottom of the document.
     */
    maybeLoad() {
  if (this.isLoading || !this.nextUrl || !this.grid) return;

  var viewportBottom =
    window.innerHeight + (window.scrollY || window.pageYOffset);

  var gridBottom =
    this.grid.getBoundingClientRect().bottom +
    (window.scrollY || window.pageYOffset);

  if (viewportBottom >= gridBottom - LOAD_THRESHOLD_PX) {
    this.loadNextPage();
  }
}

    /**
     * Fetch the next page, append its product cards, and advance nextUrl.
     * Guards against concurrent and duplicate requests, and against results
     * that arrive after a filter has reset the grid.
     */
    async loadNextPage() {
      if (this.isLoading || !this.nextUrl) return;

      var url = this.normalizeUrl(this.nextUrl);
      if (this.loadedUrls.has(url)) return; // never fetch the same page twice.

      this.isLoading = true;
      this.loadedUrls.add(url);
      this.setLoading(true);

      var generation = this.generation; // snapshot to detect a filter reset.
      this.abortController = new AbortController();

      try {
        var response = await fetch(this.nextUrl, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        var html = await response.text();

        // A filter/sort/search replaced the grid mid-flight → drop stale result.
        if (generation !== this.generation) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        this.appendItems(doc);
        this.nextUrl = this.readNextUrl(doc); // advance, or null on the last page.
      } catch (error) {
        if (error && error.name === 'AbortError') return; // superseded by a filter.
        // Transient failure: release the URL so it can be retried on next scroll.
        this.loadedUrls.delete(url);
        console.warn('[infinite-scroll] failed to load next page:', error);
      } finally {
        // Only settle state if this request still owns the current generation.
        if (generation === this.generation) {
          this.isLoading = false;
          this.setLoading(false);
          this.abortController = null;

          if (!this.nextUrl) {
            // Last page reached → stop listening (no leaks).
            this.removeScrollListener();
          } else {
            // Keep loading while still near the bottom (tall viewport / short page).
            this.maybeLoad();
          }
        }
      }
    }

    /**
     * Extract the product <li> nodes from a fetched document and append them to
     * the live grid. Cancels reveal-on-scroll animation on the new cards exactly
     * like facets.js, so they display immediately (never stuck at opacity:0).
     * @param {Document} doc - parsed HTML of the fetched next page.
     */
    appendItems(doc) {
      var newGrid = doc.querySelector(SELECTORS.grid);
      if (!newGrid || !this.grid) return;

      var items = newGrid.querySelectorAll(':scope > ' + SELECTORS.gridItem);
      var fragment = document.createDocumentFragment();

      items.forEach(function (item) {
        var clone = document.importNode(item, true);
        if (clone.classList.contains('scroll-trigger')) {
          clone.classList.add('scroll-trigger--cancel');
        }
        clone.querySelectorAll('.scroll-trigger').forEach(function (el) {
          el.classList.add('scroll-trigger--cancel');
        });
        fragment.appendChild(clone);
      });

      this.grid.appendChild(fragment);
    }

    /**
     * Read the next-page URL from a fetched document. Sourced from the
     * data-next-url attribute (rendered from paginate.next.url, so it carries
     * any active filter/sort params). Returns null on the last page.
     * @param {Document|Element} root - parsed document to read from.
     * @returns {string|null}
     */
    readNextUrl(root) {
      var scope = root.querySelector(SELECTORS.scope);
      return scope ? this.cleanUrl(scope.getAttribute('data-next-url')) : null;
    }

    /**
     * Show or hide the loading indicator.
     * @param {boolean} isLoading
     */
    setLoading(isLoading) {
      if (this.indicator) this.indicator.hidden = !isLoading;
    }

    /**
     * Hide the server-rendered numbered pagination. It stays in the DOM (SEO +
     * no-JavaScript fallback); this only removes it from view now that JS-driven
     * infinite scrolling is active.
     */
    hidePagination() {
      var pagination = this.container.querySelector(SELECTORS.pagination);
      if (pagination) pagination.style.display = 'none';
    }

    /** Attach the passive scroll/resize listener (idempotent). */
    attachScrollListener() {
      if (this.scrollAttached) return;
      this.scrollAttached = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }

    /** Remove the scroll/resize listener (last page / teardown — no leaks). */
    removeScrollListener() {
      if (!this.scrollAttached) return;
      this.scrollAttached = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    /**
     * Normalise a URL to an absolute origin+path+query string for reliable
     * comparison in the per-URL guard.
     * @param {string} url
     * @returns {string}
     */
    normalizeUrl(url) {
      try {
        var u = new URL(url, window.location.origin);
        return u.origin + u.pathname + u.search;
      } catch (error) {
        return url;
      }
    }

    /**
     * Trim an attribute value and coerce empty strings to null.
     * @param {string|null} value
     * @returns {string|null}
     */
    cleanUrl(value) {
      if (!value) return null;
      var trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    }
  };

  /**
   * Boot a single controller once the collection grid is in the DOM.
   * Guarded so it can never initialise twice on the same container.
   */
  function boot() {
    var container = document.querySelector(SELECTORS.container);
    if (!container || container.dataset.infiniteScrollBooted === 'true') return;
    container.dataset.infiniteScrollBooted = 'true';
    new CollectionInfiniteScroll(container).start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
